// Profil métier du tenant courant (business_model) — gating front SANS slug en dur.
// La valeur est persistée dans localStorage par OmniHub au chargement/switch de tenant
// (depuis la réponse /api/platform/user/tenants). "installateur" = pose CEE (OMV, Climat).

export function getBusinessModel() {
  return localStorage.getItem("current_business_model") || "";
}

// 🔑 DEC-104 — les CINQ verticaux. `business_model` est l'autorité canonique du
// métier ; ⛔ aucun `business_vertical` parallèle n'a été créé (le champ existait
// déjà et 21 fichiers le lisent).
export const VERTICAUX = ["amo", "renovation", "installateur", "omv", "regie"];

// ⛔ État EXPLICITE d'un tenant non classé — ⛔ il n'hérite d'AUCUNE conduite.
// ABSENCE DE CONFIGURATION ≠ CONFIGURATION AMO (DEC-084 appliquée au métier).
export const NON_RENSEIGNE = "non_renseigne";

// 🔑 Verticaux partageant la conduite « poseur CEE ». ⚠️ Tester l'APPARTENANCE,
// ⛔ jamais l'égalité à "installateur" : sinon donner son vertical propre à un
// tenant lui change sa conduite. Mesuré le 2026-09-06 — `omv_france` porte 138
// devis `climat` sans `snapshot_aides` ; le basculer sur `omv` avec un test
// d'égalité aurait réactivé le contrôle des aides et bloqué leur verrouillage.
export const FAMILLE_INSTALLATEUR = ["installateur", "omv"];

// True si le tenant courant relève de la FAMILLE installateur (numéro de devis
// manuel, moteur cumac, secteur via fiche...).
export function isInstallateurProfile() {
  return FAMILLE_INSTALLATEUR.includes(getBusinessModel());
}

// Le vertical est-il EXPLICITEMENT établi ? Sert aux écrans qui doivent afficher
// « non classé » plutôt que d'inventer un métier.
export function estRenseigne() {
  return VERTICAUX.includes(getBusinessModel());
}

// 🔴 LE PARCOURS DE CRÉATION DE DOSSIER — DÉCLARÉ PAR LE SERVEUR, ⛔ JAMAIS DÉDUIT.
//
// ⚠️ TROIS NOTIONS QU'ON CONFONDAIT SUR UNE SEULE LIGNE :
//     `const isOMV = isInstallateurProfile()`
//   · le TENANT ................ QUI c'est               (slug)
//   · le BUSINESS MODEL ........ ce qu'il VEND           (amo, omv, installateur…)
//   · le PROFIL DE CRÉATION .... le PARCOURS qu'il suit  (ci-dessous)
//   Trois noms, trois notions, un seul booléen — et c'est ainsi qu'on a fini
//   par écrire `if (!isOMV) → wizard TEF` : « pas installateur » était devenu
//   « donc AMO ANAH ».
//
// 🔴 CE QUE CETTE FONCTION ⛔ NE FAIT PAS : elle ⛔ ne DÉRIVE RIEN du business
//   model. Deux tenants du même modèle peuvent créer leurs dossiers
//   différemment ; les lier recréerait la déduction qu'on supprime.
//
// 🔑 `null` EST UNE RÉPONSE, ⛔ pas une panne : « ce tenant ⛔ n'a PAS déclaré de
//   parcours ». L'appelant DOIT le dire à l'écran — ⛔ jamais choisir à sa place.
//   ⚠️ `localStorage` ⛔ n'est ICI qu'un CACHE D'AFFICHAGE : l'autorité est
//   `config.dossier_creation_profile`, servi par `/api/platform/user/tenants`.
//   Un cache absent ⇒ on refuse ; ⛔ on ⛔ n'invente PAS un métier.
export function getDossierCreationProfile() {
  const v = localStorage.getItem("current_dossier_creation_profile");
  return v && v.trim() ? v.trim() : null;
}

// 🔴 L'ACTIVITÉ QUE PORTENT LES DOSSIERS DU TENANT — DÉCLARÉE par le serveur.
// ⚠️ ⛔ NE PAS confondre avec `activites_disponibles` de `/api/metier/tenant` :
// celui-là est DÉRIVÉ des modules activés, sur la liste EN DUR ["tef","climat"].
// Un tenant qui n'a NI l'un NI l'autre y reçoit tout à False — et c'est ce qui
// faisait retomber l'écran sur le littéral "tef".
// 🔑 `null` est une RÉPONSE : l'écran le dit, ⛔ il ⛔ ne choisit PAS.
export function getDossierActivite() {
  const v = localStorage.getItem("current_dossier_activite");
  return v && v.trim() ? v.trim() : null;
}

// 🔴 CAPABILITY « MISSIONS AMO » — DÉCLARÉE PAR LE SERVEUR, ⛔ JAMAIS DÉDUITE.
//
// ⚠️ CE QU'ELLE REMPLACE, et pourquoi c'était faux. `CataloguePage` affichait
//   l'onglet « Missions AMO » sur `{!isOmv && …}` — « TOUT LE MONDE SAUF OMV ».
//   Mesuré : `mario_bat` n'est ni TEF ni OMV, il héritait donc d'un concept
//   AMO / MaPrimeAdapt' qui ⛔ ne le concerne PAS, et le voyait à l'écran.
//   🔑 C'est la CINQUIÈME occurrence de la règle EN CREUX : « tout sauf X »
//   change de sens le jour où un X de plus apparaît — et personne ⛔ ne relit
//   la condition ce jour-là.
//
// 🔑 L'AUTORITÉ est celle qu'applique DÉJÀ le backend
//   (`metier_hub/tef/pdf_tenant_config.py`) : les missions AMO n'ont de sens
//   que pour un tenant portant le MODULE AMO. `/api/platform/user/tenants` sert
//   désormais ce fait sous `amo_missions` ; ⛔ l'écran ⛔ ne le devine plus.
//   ⚠️ `localStorage` ⛔ n'est ICI qu'un CACHE D'AFFICHAGE : absent ⇒ on ⛔
//   n'affiche PAS. Refuser par défaut est le seul défaut sûr pour une
//   fonctionnalité d'un AUTRE métier.
export function hasAmoMissions() {
  return localStorage.getItem("current_amo_missions") === "1";
}

// 🔴 CAPACITÉ « PARCOURS AIDES / AMO » — c'est ELLE qui compose la fiche dossier.
// ⚠️ Elle remplace `isTEFRole` (un nom qui MENT : il teste admin/super_admin/
// manager). Un dossier Mario recevait MaPrimeAdapt', Compte ANAH, DAL, Finance
// AMO et Conformité ANAH parce que celui qui le REGARDAIT était administrateur.
// 🔑 Le rôle gouverne les DROITS ; la capacité gouverne la COMPOSITION.
// ⚠️ Absente ⇒ ⛔ AUCUN bloc AMO. L'absence de déclaration ⛔ n'est PAS une
// permission d'hériter de TEF.
export function hasDossierAmo() {
  return localStorage.getItem("current_dossier_amo") === "1";
}

// 🔴 CAPACITÉ « DEVIS DEPUIS CATALOGUE » — DÉCLARÉE par le tenant.
// ⚠️ Remplace `tenants: ["climatpac","omv_france"]` sur l'entrée de menu
// « Devis » — HUITIÈME liste en dur de la semaine. Mario vend ses travaux sur
// devis et ⛔ ne voyait PAS l'entrée ; TEF, lui, a un autre parcours (AMO).
// 🔑 TEF est exclu parce qu'il ⛔ NE DÉCLARE PAS la capacité, ⛔ pas parce qu'on
// l'a nommé. Absente ⇒ ⛔ pas d'entrée : défaut sûr.
export function hasDevisCatalogue() {
  return localStorage.getItem("current_devis_catalogue") === "1";
}

// 🔴🔴 N°690-2 — CAPACITÉ « SOUS-TRAITANCE DE LA POSE » — DÉCLARÉE.
// ⚠️ MESURÉ chez Mario : le bloc « Installateur » (assignation d'un poseur,
// devis installateur, montant travaux) ⛔ n'était gaté par RIEN. Or un artisan
// qui POSE LUI-MÊME ⛔ n'a PERSONNE à qui sous-traiter : c'est un module d'un
// autre métier, posé sous ses yeux au milieu de son dossier.
// 🔑 La capacité EXISTAIT en base depuis la migration 058 (tef, omv_france,
// climatpac à True) et ⛔ n'était servie à PERSONNE — 0 occurrence côté
// frontend. ⛔ Absente ⇒ ⛔ pas de bloc : défaut sûr, et ⛔ aucun slug.
export function hasDevisInstallateur() {
  return localStorage.getItem("current_devis_installateur") === "1";
}

// 🔴 CLI-17 — L'INSTALLATEUR INTERNE EST-IL LE DÉFAUT DU DEVIS ?
//    DÉCLARÉ (`config.devis_installateur_interne_par_defaut`).
//
// ⚠️ CE QUE ÇA REMPLACE : `DevisWizard.jsx:476` testait
//    `tenantId === "climatpac"` pour pré-sélectionner ET auto-affecter
//    l'installateur interne — un métier écrit dans un NOM DE TENANT, donc faux
//    le jour où un second installateur veut le même défaut, et invisible pour
//    qui lit la configuration du tenant.
// 🔑 C'est un DÉFAUT, ⛔ PAS UN VERROU : le choix d'un SOUS-TRAITANT
//    (installateur externe) reste entier — l'étape Installateur liste TOUS les
//    installateurs et `assignerInstallateur` en accepte n'importe lequel.
// ⛔ Absente ⇒ aucune pré-sélection : OMV, TEF, Mario et Étincelle gardent
//    l'EXISTANT par construction.
export function hasInstallateurInterneParDefaut() {
  return localStorage.getItem("current_devis_installateur_interne_par_defaut") === "1";
}

// 🔴 CAPABILITÉ « PENNYLANE » — le tenant a-t-il RÉELLEMENT l'intégration ?
// Source : feature flag tenant `integration.pennylane` (payload
// /platform/user/tenants). ⛔ Aucun contournement super-admin : c'est une
// propriété du TENANT. Absente ⇒ false (INCONNU ≠ ACTIVÉ).
export function hasIntegrationPennylane() {
  return localStorage.getItem("current_integration_pennylane") === "1";
}

// 🔴 CAPABILITÉ « VISITE TERRAIN » — sert à MONTRER L'ENTRÉE vers /mobile/visite.
// ⚠️ Sans elle, l'écran terrain était SERVI mais INATTEIGNABLE depuis l'appli.
// 🔑 Déclarée par la DONNÉE (le tenant a des profils de visite), ⛔ jamais par un
// slug en dur. Absente ⇒ on ⛔ n'affiche PAS : défaut sûr.
// 🔴 L2 (N°876) — le tenant a-t-il ADOPTÉ le modèle Site (un dossier = 1 client
// + 1 site) ? DÉCLARÉ (`tenants.config.dossier_site_requis`) ; absent ⇒ ⛔ rien ne
// change pour ce tenant.
export function hasDossierSite() {
  return localStorage.getItem("current_dossier_site_requis") === "1";
}

// 🔴 CHANTIER — le code du profil de relevé DÉCLARÉ par le tenant
// (`tenants.config.profil_chantier`). Absent ⇒ null ⇒ ⛔ aucun relevé chantier.
// ⛔ Jamais de profil par défaut : un profil deviné serait un métier deviné.
export function profilChantier() {
  try { return localStorage.getItem("current_profil_chantier") || null; } catch { return null; }
}

export function hasVisitesTerrain() {
  return localStorage.getItem("current_visites_terrain") === "1";
}

// Segments commerciaux du tenant courant (liste b2c/b2b), persistés par OmniHub/
// AuthContext depuis /api/platform/user/tenants. Sert de PRESET au défaut de saisie
// type_client ; la primitive is_b2b reste pilotée par le client réel.
export function getSegments() {
  try {
    return JSON.parse(localStorage.getItem("current_segments") || "[]");
  } catch {
    return [];
  }
}

// Régime d'aides du tenant courant (liste : "mpr", "cee", "anah"), persisté par
// OmniHub depuis /api/platform/user/tenants → config.regime_aides. Source du gating
// MPR par RÉGIME (jamais par slug). Défaut sûr = [] si absent/illisible.
export function getRegimeAides() {
  try {
    return JSON.parse(localStorage.getItem("current_regime_aides") || "[]");
  } catch {
    return [];
  }
}

// 🔴 N°751 — `aProfilAmoAnah` A ÉTÉ SUPPRIMÉE ICI, VOLONTAIREMENT.
//
// Elle dérivait le profil AMO du RÉGIME D'AIDES (`config.regime_aides`, `tef` → ["anah"]).
// `hasDossierAmo()` ci-dessus répond à LA MÊME question par la capacité DÉCLARÉE
// `config.dossier_amo` (migration 057), et elle était déjà intégrée et servie.
// ⇒ Garder les deux aurait fait DEUX autorités pour un seul verdict. Arbitrage JJ N°751,
//   décidé avec CC DEV : « une autorité, ⛔ pas trois ».
//
// ⚠️ CE QUI A RENDU LE RETRAIT SÛR, et ⛔ pas l'inverse : l'ancienne formule
//    `dossier.activite === "tef" || tenantId === "tef"` était VRAIE pour un dossier
//    d'activité « tef » chez UN AUTRE TENANT — les **9 dossiers du tenant `demo`** (9 en
//    PROD, 9 en PREPROD), alors que `config.dossier_amo` n'est déclaré que par `tef`.
//    La migration `063` pose donc `dossier_amo` sur `demo` **AVANT** ce frontend.
// ⛔ NE PAS réintroduire une dérivation par slug ni par régime pour ce verdict : le gating
//    par RÉGIME reste légitime pour ce qui EST un régime (MPR/CEE via `getRegimeAides`),
//    ⛔ pas pour composer la fiche dossier.

// Défaut type_client dérivé des segments du tenant : un tenant PUREMENT b2b
// (ex OMV) pré-sélectionne "professionnel" ; sinon (b2c, mixte, ou inconnu) on
// garde le défaut sûr "particulier". Pur / testable.
export function defaultClientTypeForSegments(segments) {
  const s = Array.isArray(segments) ? segments : [];
  if (s.length === 1 && s[0] === "b2b") return "professionnel";
  return "particulier";
}

export function defaultClientType() {
  return defaultClientTypeForSegments(getSegments());
}

// T-482 — le tenant déclare-t-il MaPrimeRénov' dans son régime d'aides (`config.regime_aides`) ? Gate de l'écran « Suivi MaPrimeRénov' » (⛔ aucun slug).
export function hasAidesMpr() {
  return getRegimeAides().map((r) => String(r).toLowerCase()).includes("mpr");
}
